import React, { useState, useEffect, useMemo } from 'react';
import { X, Filter, Loader, RefreshCw } from 'lucide-react';
import { useSofteamboardStore } from '../../store/softeamboardStore';

const FilterDrawer = ({ isOpen, onClose }) => {
  const { 
    filters, 
    setFilters, 
    consultants, 
    needs,
    loading,
    error,
    loadConsultants
  } = useSofteamboardStore();

  // Memoize clients calculation to prevent infinite loops
  const clients = useMemo(() => {
    if (!needs || needs.length === 0) return [];
    const uniqueClients = [...new Set(needs.map(need => need.client).filter(client => client && client.trim() !== ''))];
    return uniqueClients.sort((a, b) => String(a).localeCompare(String(b)));
  }, [needs]);

  const handleSalesToggle = (userId) => {
    console.log('🔍 FilterDrawer - Toggling sales filter:', { userId, currentFilters: filters.consultants });
    
    const newSales = filters.consultants.includes(userId)
      ? filters.consultants.filter(id => id !== userId)
      : [...filters.consultants, userId];
    
    console.log('🔍 FilterDrawer - New sales filter:', newSales);
    setFilters({ consultants: newSales });
  };

  const handleClientToggle = (client) => {
    const newClients = filters.clients.includes(client)
      ? filters.clients.filter(c => c !== client)
      : [...filters.clients, client];
    setFilters({ clients: newClients });
  };

  const handlePriorityToggle = (priority) => {
    const newPriorities = filters.priorities.includes(priority)
      ? filters.priorities.filter(p => p !== priority)
      : [...filters.priorities, priority];
    setFilters({ priorities: newPriorities });
  };

  const clearAllFilters = () => {
    setFilters({
      consultants: [],
      clients: [],
      priorities: [],
      search: ''
    });
  };

  const refreshConsultants = async () => {
    try {
      console.log('🔍 FilterDrawer - Manually refreshing consultants...');
      await loadConsultants();
    } catch (error) {
      console.error('🔍 FilterDrawer - Failed to refresh consultants:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
        role="presentation"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className={`
        fixed lg:relative inset-y-0 left-0 z-30 w-80 bg-white border-r border-gray-200
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <Filter size={20} className="text-gray-600" />
              <h2 className="font-semibold text-gray-900">Filters</h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 lg:hidden"
              aria-label="Close filters"
            >
              <X size={20} />
            </button>
          </div>

          {/* Filter Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            
            {/* Sales Filter */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-gray-900">
                  Sales ({consultants.length})
                </h3>
                {consultants.length === 0 && !loading && (
                  <button
                    onClick={refreshConsultants}
                    className="p-1 text-gray-400 hover:text-gray-600"
                    title="Refresh consultants"
                  >
                    <RefreshCw size={14} />
                  </button>
                )}
              </div>
              
              {loading ? (
                <div className="flex items-center text-sm text-gray-500">
                  <Loader className="animate-spin mr-2" size={14} />
                  Loading sales users...
                </div>
              ) : error && consultants.length === 0 ? (
                <div className="text-sm text-red-600 p-3 bg-red-50 rounded-lg">
                  <p className="font-medium">Failed to load sales users</p>
                  <p className="text-xs mt-1">{error}</p>
                  <button
                    onClick={refreshConsultants}
                    className="mt-2 text-xs text-red-700 hover:text-red-900 underline"
                  >
                    Try again
                  </button>
                </div>
              ) : consultants.length === 0 ? (
                <div className="text-sm text-gray-500 p-3 bg-gray-50 rounded-lg">
                  <p>No sales users found</p>
                  <p className="text-xs mt-1">Make sure users with role "Sales" exist</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {consultants.map(user => (
                    <label key={user.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={filters.consultants.includes(user.id)}
                        onChange={() => handleSalesToggle(user.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 flex-1">{user.name}</span>
                      {filters.consultants.includes(user.id) && (
                        <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                          Active
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              )}
              
              {/* Show active consultant filters */}
              {filters.consultants.length > 0 && (
                <div className="mt-2 text-xs text-blue-600">
                  Filtering by {filters.consultants.length} consultant{filters.consultants.length > 1 ? 's' : ''}
                </div>
              )}
            </div>

            {/* Clients Filter */}
            <div>
              <h3 className="font-medium text-gray-900 mb-3">
                Clients ({clients.length})
              </h3>
              {loading ? (
                <div className="flex items-center text-sm text-gray-500">
                  <Loader className="animate-spin mr-2" size={14} />
                  Loading clients...
                </div>
              ) : clients.length === 0 ? (
                <div className="text-sm text-gray-500 p-3 bg-gray-50 rounded-lg">
                  No clients found
                </div>
              ) : (
                <div className="space-y-3 max-h-64 overflow-y-auto p-1 custom-scrollbar">
                  {clients.map(client => (
                    <label key={client} className="flex items-center space-x-3 cursor-pointer py-1 hover:bg-gray-50 px-1 rounded">
                      <input
                        type="checkbox"
                        checked={filters.clients.includes(client)}
                        onChange={() => handleClientToggle(client)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2"
                      />
                      <span className="text-sm text-gray-700 flex-1">{client}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Priority Filter */}
            <div>
              <h3 className="font-medium text-gray-900 mb-3">Priority</h3>
              <div className="space-y-2">
                {['P1', 'P2', 'P3'].map(priority => (
                  <label key={priority} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={filters.priorities.includes(priority)}
                      onChange={() => handlePriorityToggle(priority)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{priority}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Full-text search */}
            <div>
              <h3 className="font-medium text-gray-900 mb-3">Search</h3>
              <input
                type="text"
                placeholder="Search in titles, descriptions..."
                value={filters.search}
                onChange={e => setFilters({ search: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                aria-label="Search in titles and descriptions"
              />
            </div>

            {/* Clear Filters */}
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={clearAllFilters}
                className="w-full px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Clear All Filters
              </button>
            </div>

            {/* Temporary Debug Section */}
            {process.env.NODE_ENV === 'development' && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h4 className="text-sm font-medium text-yellow-800 mb-2">Debug Info</h4>
                <button
                  onClick={() => {
                    console.log('🧪 Full consultant data:', consultants);
                    console.log('🧪 Current filters:', filters);
                    console.log('🧪 Needs with consultants:', needs.filter(n => n.consultants && n.consultants.length > 0));
                    console.log('🧪 Debug info logged to console');
                  }}
                  className="px-2 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700"
                >
                  Debug Consultants
                </button>
              </div>
            )}

            {/* Enhanced Debug Info */}
            {process.env.NODE_ENV === 'development' && isOpen && (
              <div className="text-xs text-gray-400 p-2 bg-gray-50 rounded space-y-1">
                <div>Debug: {consultants.length} consultants, {clients.length} clients, {needs.length} needs</div>
                <div>Active filters: C:{filters.consultants.length}, Cl:{filters.clients.length}, P:{filters.priorities.length}</div>
                <div>Needs with consultants: {needs.filter(n => n.consultants && n.consultants.length > 0).length}</div>
                <div>First consultant: {consultants[0] ? `${consultants[0].name} (${consultants[0].id})` : 'None'}</div>
                {error && <div className="text-red-500">Error: {error}</div>}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterDrawer;